import type { DeptGroupedTreeNode } from '@/types/api';

/**
 * 机构 ID 统一为 string，与机构树节点 id 类型一致，避免 TreeSelect 无法匹配标签而显示原始数字
 */
export function normalizeInstitutionId(
  id: string | number | null | undefined,
): string | undefined {
  if (id === null || id === undefined || id === '') {
    return undefined;
  }
  return String(id);
}

/** 是否总机构（id = '1'） */
export function isRootInstitutionId(id: string | number | null | undefined): boolean {
  return normalizeInstitutionId(id) === '1';
}

/**
 * 机构树节点类型（兼容不同的数据结构）
 */
export interface OrganizationNode {
  id?: string | number;
  organizationName?: string;
  children?: OrganizationNode[];
  [key: string]: any;
}

/**
 * 机构选择器选项类型
 */
export interface OrganizationOption {
  title?: string;
  label?: string;
  value: string | number;
  key?: string | number;
  children?: OrganizationOption[];
}

/**
 * 使用 label 的机构选择器选项类型
 */
export interface OrganizationOptionWithLabel {
  label: string;
  value: string | number;
  children?: OrganizationOptionWithLabel[];
}

/**
 * 从机构树中递归查找指定ID的机构节点
 * @param nodes 机构树节点数组
 * @param targetId 目标机构ID
 * @returns 找到的机构节点，如果未找到返回 null
 */
export function findOrganizationNode(
  nodes: OrganizationNode[],
  targetId: string | number
): OrganizationNode | null {
  if (!Array.isArray(nodes)) {
    return null;
  }

  for (const node of nodes) {
    if (String(node.id) === String(targetId)) {
      return node;
    }
    if (node.children && node.children.length > 0) {
      const found = findOrganizationNode(node.children, targetId);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/**
 * 构建机构选择器选项（根据用户机构ID过滤）
 * - 如果用户机构ID = '1'（总机构），显示完整的机构树
 * - 如果用户机构ID != '1'（子机构），只显示当前用户所在的机构
 * 
 * @param nodes 机构树节点数组
 * @param currentUserInstitutionId 当前用户的机构ID
 * @param options 选项配置
 * @param options.useLabel 是否使用 label 字段（默认使用 title）
 * @returns 过滤后的机构选择器选项数组
 */
export function buildFilteredOrganizationOptions(
  nodes: OrganizationNode[],
  currentUserInstitutionId: string | number | null | undefined,
  options: { useLabel?: boolean } = {}
): OrganizationOption[] {
  const { useLabel = false } = options;

  if (!Array.isArray(nodes)) {
    return [];
  }

  const normalizedUserId = normalizeInstitutionId(currentUserInstitutionId);

  // 如果用户是总机构（ID='1'），显示完整的机构树
  if (isRootInstitutionId(normalizedUserId)) {
    return nodes.map(node => {
      const nodeId = normalizeInstitutionId(node.id) || '';
      const option: OrganizationOption = {
        value: nodeId,
        key: nodeId,
      };

      const orgName = node.organizationName || '';
      if (useLabel) {
        option.label = orgName;
      } else {
        option.title = orgName;
      }

      if (node.children && node.children.length > 0) {
        option.children = buildFilteredOrganizationOptions(
          node.children,
          currentUserInstitutionId,
          options
        );
      }

      return option;
    });
  }

  // 如果用户是子机构，只显示当前用户所在的机构
  if (normalizedUserId) {
    const userOrg = findOrganizationNode(nodes, normalizedUserId);
    if (userOrg) {
      const nodeId = normalizeInstitutionId(userOrg.id) || '';
      const option: OrganizationOption = {
        value: nodeId,
        key: nodeId,
      };

      const orgName = userOrg.organizationName || '';
      if (useLabel) {
        option.label = orgName;
      } else {
        option.title = orgName;
      }

      if (userOrg.children && userOrg.children.length > 0) {
        option.children = buildFilteredOrganizationOptions(
          userOrg.children,
          currentUserInstitutionId,
          options
        );
      }

      return [option];
    }
  }

  // 如果没有找到用户机构，返回空数组
  return [];
}

/** 将部门树转为 TreeSelect options */
export function buildDeptTreeSelectOptions(
  nodes: DeptGroupedTreeNode[] | undefined | null,
): OrganizationOption[] {
  if (!Array.isArray(nodes)) {
    return [];
  }
  return nodes.map((node) => {
    const id = node.id !== undefined && node.id !== null ? String(node.id) : '';
    return {
      title: node.name || '未命名部门',
      value: id,
      key: id,
      children: node.children?.length
        ? buildDeptTreeSelectOptions(node.children)
        : undefined,
    };
  });
}

/** 收集 TreeSelect 树中全部节点 value */
export function collectTreeSelectValues(nodes: OrganizationOption[]): string[] {
  const ids: string[] = [];
  const walk = (list: OrganizationOption[]) => {
    for (const node of list || []) {
      if (node.value !== undefined && node.value !== null && node.value !== '') {
        ids.push(String(node.value));
      }
      if (node.children?.length) {
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return ids;
}

/** 从当前用户信息中取出部门 ID 列表 */
export function normalizeUserDepartmentIds(userInfo: {
  depts?: Array<{ id?: string | number | null }>;
} | null | undefined): string[] {
  if (!Array.isArray(userInfo?.depts)) {
    return [];
  }
  return userInfo.depts
    .map((dept) =>
      dept?.id === null || dept?.id === undefined ? '' : String(dept.id).trim(),
    )
    .filter(Boolean);
}

/** 兼容部门树接口返回单节点或节点数组 */
export function normalizeDeptTreeResult(result: unknown): DeptGroupedTreeNode[] {
  if (!result) {
    return [];
  }
  if (Array.isArray(result)) {
    return result as DeptGroupedTreeNode[];
  }
  if (typeof result === 'object') {
    return [result as DeptGroupedTreeNode];
  }
  return [];
}

